import { useCallback, useEffect, useState } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import { colors } from "../utils";

const Plot = createPlotlyComponent(Plotly);

const ScatterPlot = ({
  isLoading,
  data,
  timebucketIdx,
  activeDomains,
  activeEntities,
  activeProxies,
}) => {
  const [chartData, setChartData] = useState(null);
  const [chartLayout, setChartLayout] = useState(null);
  const [showPlot, setShowPlot] = useState(false);

  const createChartLayout = useCallback(() => {
    const chartLayout = {
      scatter: {
        bgcolor: "rgba(0,0,0,0)",
      },
      title: "",
      xaxis: {
        title: {
          text: activeDomains.length === 2 ? activeDomains[0]?.label : "",
          font: {
            size: 14,
            color: "white",
          },
          standoff: 20,
        },
        range: [0, 100],
        linecolor: "white",
        gridcolor: "rgba(255,255,255, 0.1)",
      },
      yaxis: {
        title: {
          text: activeDomains.length === 2 ? activeDomains[1]?.label : "",
          font: {
            size: 14,
            color: "white",
          },
          standoff: 20,
        },
        range: [0, 100],
        linecolor: "white",
        gridcolor: "rgba(255,255,255, 0.1)",
      },
      width: "560",
      height: "500",
      paper_bgcolor: "transparent",
      plot_bgcolor: "transparent",
      font: {
        color: "white",
      },
    };
    setChartLayout(chartLayout);
  }, [activeDomains]);

  const createChartData = useCallback(() => {
    if (!data) return;
    const nodes = [...activeEntities, ...activeProxies];
    const plotData = nodes.map((node, idx) => {
      if (node.label === undefined || data[node.label] === undefined)
        return null;

      let scoreX, scoreLbX, deltaLbX, scoreUbX, deltaUbx;
      let scoreY, scoreLbY, deltaLbY, scoreUbY, deltaUbY;

      // Create interval notation for each entity datapoint
      scoreX =
        data[node.label][activeDomains[0]?.value]?.value[
          node.label.includes(" Proxy") ? timebucketIdx : 0
        ];
      scoreLbX =
        data[node.label][activeDomains[0]?.value]?.value_lb[
          node.label.includes(" Proxy") ? timebucketIdx : 0
        ];
      deltaLbX = scoreX - scoreLbX;
      scoreUbX =
        data[node.label][activeDomains[0]?.value]?.value_ub[
          node.label.includes(" Proxy") ? timebucketIdx : 0
        ];
      deltaUbx = scoreUbX - scoreX;

      scoreY =
        data[node.label][activeDomains[1]?.value]?.value[
          node.label.includes(" Proxy") ? timebucketIdx : 0
        ];
      scoreLbY =
        data[node.label][activeDomains[1]?.value]?.value_lb[
          node.label.includes(" Proxy") ? timebucketIdx : 0
        ];
      deltaLbY = scoreY - scoreLbY;
      scoreUbY =
        data[node.label][activeDomains[1]?.value]?.value_ub[
          node.label.includes(" Proxy") ? timebucketIdx : 0
        ];
      deltaUbY = scoreUbY - scoreY;

      const intervals = [
        `${scoreX} [${scoreLbX}, ${scoreUbX}]`,
        `${scoreY} [${scoreLbY}, ${scoreUbY}]`,
      ];

      // Generate chart data objects for value, upper bound and lower bound
      return {
        x: [scoreX],
        y: [scoreY],
        error_x: {
          type: "data",
          array: [deltaUbx],
          arrayminus: [deltaLbX],
          visible: true,
          thickness: 2,
          width: 4,
          color: node.label.includes(" Proxy")
            ? "rgba(255,255,255,0.4)"
            : colors[idx].replace("rgb", "rgba").replace(")", ",0.4)"),
        },
        error_y: {
          type: "data",
          array: [deltaUbY],
          arrayminus: [deltaLbY],
          visible: true,
          thickness: 2,
          width: 4,
          color: node.label.includes(" Proxy")
            ? "rgba(255,255,255,0.4)"
            : colors[idx].replace("rgb", "rgba").replace(")", ",0.4)"),
        },
        mode: "markers",
        type: "scatter",
        marker: {
          size: 8,
          color: node.label.includes(" Proxy")
            ? "rgba(255,255,255,1)"
            : colors[idx],
        },
        customdata: [intervals],
        name: node.label,
        hovertemplate: `<b>%{data.name}</b><br>${activeDomains[0].label}: %{customdata[0]}<br>${activeDomains[1].label}: %{customdata[1]}<extra></extra>`,
      };
    });
    setChartData(plotData);
  }, [activeDomains, activeEntities, activeProxies, data, timebucketIdx]);

  useEffect(() => {
    if (
      isLoading ||
      !data ||
      (activeProxies.length === 0 && activeEntities.length === 0) ||
      activeDomains.length < 2
    ) {
      setShowPlot(false);
      setChartLayout(null);
      setChartData(null);
      return;
    }

    setShowPlot(true);
    createChartData();
    createChartLayout();
  }, [
    activeDomains,
    activeEntities,
    activeProxies,
    data,
    createChartData,
    createChartLayout,
    isLoading,
  ]);

  return (
    <div className="flex flex-col items-center content-center min-w-50">
      {isLoading && <p className="text-center">Loading data...</p>}
      {!isLoading && chartLayout && chartData && showPlot && (
        <Plot data={chartData} layout={chartLayout} />
      )}
    </div>
  );
};

export default ScatterPlot;
